import React, { useCallback, useEffect, useState } from 'react';

import { Avatar } from '@backstage/core-components';
import { alertApiRef, useApi } from '@backstage/core-plugin-api';

import {
  Box,
  Button,
  Divider,
  IconButton,
  TextField,
  Typography,
  makeStyles,
} from '@material-ui/core';
import ChatBubbleOutlineIcon from '@material-ui/icons/ChatBubbleOutline';
import DeleteOutlineIcon from '@material-ui/icons/DeleteOutline';
import EditIcon from '@material-ui/icons/Edit';
import FavoriteIcon from '@material-ui/icons/Favorite';
import FavoriteBorderIcon from '@material-ui/icons/FavoriteBorder';

import { useIdeaEngagement } from '../../context/IdeaEngagementContext';
import { ConfirmationDialog } from '../ConfirmationDialog/ConfirmationDialog';

/**
 * Likes and comments for a single idea. State and persistence are owned by
 * IdeaEngagementContext, which talks to the tomtom-lab backend; this component
 * only renders and dispatches.
 */

type Props = {
  ideaId?: number;
  currentUserRef?: string;
};

const useStyles = makeStyles(theme => ({
  root: {
    marginTop: theme.spacing(2),
  },
  sectionTitle: {
    fontWeight: 600,
    marginBottom: theme.spacing(1),
  },
  actionsRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  metric: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    color: theme.palette.text.secondary,
  },
  likeButtonActive: {
    color: theme.palette.error.main,
  },
  commentForm: {
    display: 'flex',
    gap: theme.spacing(1),
    alignItems: 'flex-start',
    marginBottom: theme.spacing(2),
  },
  commentList: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
  },
  commentRow: {
    display: 'flex',
    gap: theme.spacing(1.5),
    alignItems: 'flex-start',
  },
  commentBody: {
    flex: 1,
  },
  commentHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  commentMeta: {
    display: 'flex',
    alignItems: 'baseline',
    gap: theme.spacing(1),
  },
  commentActions: {
    marginLeft: 'auto',
    display: 'flex',
    gap: theme.spacing(0.5),
  },
  actionIcon: {
    padding: theme.spacing(0.5),
  },
  editForm: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
    marginTop: theme.spacing(0.5),
  },
  editActions: {
    display: 'flex',
    gap: theme.spacing(1),
    justifyContent: 'flex-end',
  },
  commentAuthor: {
    fontWeight: 600,
  },
  commentTime: {
    color: theme.palette.text.secondary,
  },
  emptyState: {
    color: theme.palette.text.secondary,
    fontStyle: 'italic',
  },
}));

const toDisplayName = (userRef?: string): string => {
  if (!userRef) return 'You';
  const withoutNamespace = userRef.replace(/^user:[^/]*\//, '');
  const localPart = withoutNamespace.split('@')[0].replace(/_tomtom.*$/, '');
  return localPart
    .split(/[._]/)
    .filter(Boolean)
    .map(part => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
};

const formatTime = (date: Date): string =>
  // eslint-disable-next-line new-cap
  Intl.DateTimeFormat('en-us', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);

export const IdeaEngagement = ({ ideaId, currentUserRef }: Props) => {
  const classes = useStyles();

  const {
    liked,
    likeCount,
    comments,
    commentsLoaded,
    loadComments,
    toggleLike,
    addComment,
    editComment,
    deleteComment,
  } = useIdeaEngagement(ideaId);
  const alertApi = useApi(alertApiRef);
  const [draft, setDraft] = useState('');
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editDraft, setEditDraft] = useState('');
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [submitting, setSubmitting] = useState(false);

  /**
   * Mutations are optimistic and self-rolling-back, so a handler only has to
   * report the failure.
   *
   * The backend cause is deliberately not surfaced: ResponseError carries the
   * serialized response body, and the user is shown only what they were trying
   * to do. The routers log the real error for troubleshooting.
   */
  const report = useCallback(
    (message: string) => () => {
      alertApi.post({ message, severity: 'error' });
    },
    [alertApi],
  );

  /**
   * Comments are only needed once this panel is on screen, so they are fetched
   * here rather than alongside the ideas list.
   */
  useEffect(() => {
    if (ideaId !== undefined && !commentsLoaded) {
      loadComments().catch(report('Failed to load comments'));
    }
  }, [ideaId, commentsLoaded, loadComments, report]);

  // The draft is only cleared once the comment is stored, so a failed request
  // does not silently discard what the user typed.
  const handleAddComment = async () => {
    const text = draft.trim();
    if (!text || submitting) return;

    setSubmitting(true);
    try {
      await addComment(text);
      setDraft('');
    } catch {
      report('Failed to add comment')();
    } finally {
      setSubmitting(false);
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    // Submit on Ctrl/Cmd + Enter for convenience.
    if ((event.ctrlKey || event.metaKey) && event.key === 'Enter') {
      event.preventDefault();
      handleAddComment();
    }
  };

  const startEdit = (commentId: number, text: string) => {
    setEditingId(commentId);
    setEditDraft(text);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditDraft('');
  };

  const saveEdit = () => {
    if (editingId === null || !editDraft.trim()) return;
    editComment(editingId, editDraft).catch(report('Failed to edit comment'));
    cancelEdit();
  };

  const handleEditKeyDown = (event: React.KeyboardEvent) => {
    if ((event.ctrlKey || event.metaKey) && event.key === 'Enter') {
      event.preventDefault();
      saveEdit();
    } else if (event.key === 'Escape') {
      event.preventDefault();
      cancelEdit();
    }
  };

  const confirmDelete = () => {
    if (deletingId !== null) {
      deleteComment(deletingId).catch(report('Failed to delete comment'));
    }
    setDeletingId(null);
  };

  return (
    <Box className={classes.root}>
      <Divider />
      <Box className={classes.actionsRow} mt={2}>
        <Button
          size="small"
          disableRipple
          startIcon={
            liked ? (
              <FavoriteIcon className={classes.likeButtonActive} />
            ) : (
              <FavoriteBorderIcon />
            )
          }
          aria-label={`${liked ? 'Remove your like from' : 'Like'} this idea, ${
            likeCount === 1 ? '1 like' : `${likeCount} likes`
          }`}
          onClick={() => toggleLike().catch(report('Failed to update like'))}
        >
          {likeCount}
        </Button>
        <Box className={classes.metric}>
          <ChatBubbleOutlineIcon fontSize="small" />
          <Typography variant="body2">{comments.length}</Typography>
        </Box>
      </Box>

      <Typography variant="subtitle1" className={classes.sectionTitle}>
        Comments
      </Typography>

      <Box className={classes.commentForm}>
        <TextField
          fullWidth
          multiline
          size="small"
          variant="outlined"
          placeholder="Add a comment…"
          value={draft}
          onChange={event => setDraft(event.target.value)}
          onKeyDown={handleKeyDown}
        />
        <Button
          variant="contained"
          color="primary"
          disabled={!draft.trim() || submitting}
          onClick={handleAddComment}
        >
          Comment
        </Button>
      </Box>

      {comments.length === 0 ? (
        <Typography variant="body2" className={classes.emptyState}>
          No comments yet. Be the first to share your thoughts.
        </Typography>
      ) : (
        <Box className={classes.commentList}>
          {comments.map(comment => {
            // Ownership is decided on the entity ref, not the rendered name:
            // display names are derived and can collide.
            const isOwn = comment.userRef === currentUserRef;
            const isEditing = editingId === comment.id;
            const authorDisplayName = toDisplayName(comment.userRef);
            return (
              <Box key={comment.id} className={classes.commentRow}>
                <Avatar displayName={authorDisplayName} />
                <Box className={classes.commentBody}>
                  <Box className={classes.commentHeader}>
                    <Box className={classes.commentMeta}>
                      <Typography
                        variant="body2"
                        className={classes.commentAuthor}
                      >
                        {authorDisplayName}
                      </Typography>
                      <Typography
                        variant="caption"
                        className={classes.commentTime}
                      >
                        {comment.createdAt &&
                          formatTime(new Date(comment.createdAt))}
                        {comment.updatedAt && ' (edited)'}
                      </Typography>
                    </Box>
                    {isOwn && !isEditing && (
                      <Box className={classes.commentActions}>
                        <IconButton
                          size="small"
                          className={classes.actionIcon}
                          aria-label="Edit comment"
                          onClick={() => startEdit(comment.id, comment.comment)}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          className={classes.actionIcon}
                          aria-label="Delete comment"
                          onClick={() => setDeletingId(comment.id)}
                        >
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    )}
                  </Box>
                  {isEditing ? (
                    <Box className={classes.editForm}>
                      <TextField
                        fullWidth
                        multiline
                        size="small"
                        variant="outlined"
                        value={editDraft}
                        onChange={event => setEditDraft(event.target.value)}
                        onKeyDown={handleEditKeyDown}
                      />
                      <Box className={classes.editActions}>
                        <Button size="small" onClick={cancelEdit}>
                          Cancel
                        </Button>
                        <Button
                          size="small"
                          variant="contained"
                          color="primary"
                          disabled={!editDraft.trim()}
                          onClick={saveEdit}
                        >
                          Save
                        </Button>
                      </Box>
                    </Box>
                  ) : (
                    <Typography variant="body2">{comment.comment}</Typography>
                  )}
                </Box>
              </Box>
            );
          })}
        </Box>
      )}

      {deletingId !== null && (
        <ConfirmationDialog
          open
          title="Delete comment"
          message={['Are you sure you want to delete this comment?']}
          handleClose={() => setDeletingId(null)}
          handleSubmit={confirmDelete}
        />
      )}
    </Box>
  );
};
